interface EnvironmentConfig {
  apiUrl: string;
  environment: string;
  isDevelopment: boolean;
  isProduction: boolean;
  isLocal: boolean;
}

type Environment = 'local' | 'staging' | 'production';

// Get environment variables with fallbacks
const API_URL = import.meta.env.VITE_API_URL;
const APP_ENV = (import.meta.env.VITE_APP_ENV || 'local') as Environment;

// Detect Cloudflare Pages environment
const CF_PAGES_BRANCH = import.meta.env.CF_PAGES_BRANCH;
const CF_PAGES_URL = import.meta.env.CF_PAGES_URL;
const isCloudflarePages = Boolean(CF_PAGES_BRANCH);

// Determine environment based on branch or explicit setting
const determineEnvironment = (): Environment => {
  // If explicitly set, use that
  if (APP_ENV && ['local', 'staging', 'production'].includes(APP_ENV)) {
    return APP_ENV;
  }
  
  // If running on Cloudflare Pages, determine by branch
  if (isCloudflarePages && CF_PAGES_BRANCH) {
    if (CF_PAGES_BRANCH === 'main' || CF_PAGES_BRANCH === 'master') {
      return 'production';
    }
    if (CF_PAGES_BRANCH === 'staging' || CF_PAGES_BRANCH === 'develop') {
      return 'staging';
    }
    // All other branches are staging
    return 'staging';
  }
  
  // Local development fallback
  if (import.meta.env.DEV) {
    return 'local';
  }
  
  return 'staging';
};

const currentEnv = determineEnvironment();

// Environment-specific configurations
const envConfig: Record<Environment, EnvironmentConfig> = {
  local: {
    apiUrl: API_URL || 'http://localhost:8000/api/v1',
    environment: 'local',
    isDevelopment: true,
    isProduction: false,
    isLocal: true
  },
  staging: {
    apiUrl: API_URL || 'https://staging-api.linklibrary.ai/api/v1',
    environment: 'staging',
    isDevelopment: true,
    isProduction: false,
    isLocal: false
  },
  production: {
    apiUrl: API_URL || 'https://api.linklibrary.ai/api/v1',
    environment: 'production',
    isDevelopment: false,
    isProduction: true,
    isLocal: false
  }
};

// Get current environment configuration
export const API_CONFIG = envConfig[currentEnv];

// Export the API URL
export const API_BASE_URL = API_CONFIG.apiUrl;

// Ensure the URL doesn't end with a trailing slash
export const normalizedApiBaseUrl = API_BASE_URL.replace(/\/$/, '');

// Export environment helpers
export const ENV = {
  current: currentEnv,
  isDev: API_CONFIG.isDevelopment,
  isProd: API_CONFIG.isProduction,
  isLocal: API_CONFIG.isLocal,
  branch: CF_PAGES_BRANCH || 'unknown',
  isCloudflarePages
};

// Log configuration in non-production environments
if (!API_CONFIG.isProduction) {
  console.log('🚀 Environment Configuration:');
  console.log('  Environment:', API_CONFIG.environment);
  console.log('  API URL:', API_BASE_URL);
  console.log('  Branch:', CF_PAGES_BRANCH || 'local');
  console.log('  Cloudflare Pages:', isCloudflarePages);
}